import { useMemo, useState } from "react";
import {
  Button,
  CardNotification,
  Heading,
  RoundButton,
  Table,
  type TableColumn,
  Text,
  TextField,
} from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import GroupCodeInput from "@modules/vacancies/Groups/group-code-input";
import type { IErrorWarning } from "@models/Errors/ErrorWarning";
import { selectNewLeagueConfigurations } from "../../store/packageWithLeaguesStore";
import usePackageWithLeaguesStore from "../../hooks/use-package-with-leagues-store";
import usePackageWithLeaguesStoreApi from "../../hooks/use-package-with-leagues-store-api";
import type { SubjectConfiguration } from "../../types";
import { getLockedVacancies } from "../../utils/group-vacancy-source";
import { validateInitialGroupConfiguration } from "../../utils/wizard-validation";
import ExistingLeagueGroupsSummary from "../existing-league-groups-summary";
import PackageConfigurationSummary from "../package-configuration-summary";
import ValidationMessageList from "../validation-message-list";
import css from "./initialGroupsStep.module.scss";

interface InitialGroupsStepProps {
  onBack: () => void;
  onCreate: () => Promise<unknown>;
  isSubmitting: boolean;
}

const InitialGroupsStep = ({ onBack, onCreate, isSubmitting }: InitialGroupsStepProps) => {
  const { t } = useTranslation();
  const store = usePackageWithLeaguesStoreApi();
  const packageDetails = usePackageWithLeaguesStore(state => state.packageDetails);
  const subjects = usePackageWithLeaguesStore(state => state.subjects);
  const rows = useMemo(
    () => subjects.filter(configuration => configuration.choice.kind !== "omit"),
    [subjects],
  );
  const newLeagueRows = useMemo(() => selectNewLeagueConfigurations({ subjects }), [subjects]);
  const messagesByCourseId = usePackageWithLeaguesStore(
    state => state.validationMessagesByCourseId,
  );

  const globalMessages = usePackageWithLeaguesStore(state => state.globalValidationMessages);
  const [formatErrors, setFormatErrors] = useState<Record<string, IErrorWarning[]>>({});

  const affectedRows = useMemo(
    () =>
      newLeagueRows.filter(row =>
        (messagesByCourseId[row.subject.courseId] ?? []).some(message => message.discardable),
      ).length,
    [messagesByCourseId, newLeagueRows],
  );
  const columns = useMemo<TableColumn[]>(
    () => [
      {
        key: "subject",
        header: t("package-wizard.groups.subject"),
        renderCell: data => {
          const row = data as SubjectConfiguration;
          return (
            <Text>
              {row.subject.code} - {row.subject.name}
            </Text>
          );
        },
        desktopOptions: {
          headCellClassName: css.initialGroupsStep_subjectColumn,
        },
      },
      {
        key: "code",
        header: t("package-wizard.groups.code"),
        renderCell: data => {
          const row = data as SubjectConfiguration;
          if (row.choice.kind === "use-existing") {
            return <ExistingLeagueGroupsSummary groupCodes={row.choice.existingGroupCodes} />;
          }

          if (row.choice.kind !== "create-new") return null;

          const messages = (messagesByCourseId[row.subject.courseId] ?? []).filter(
            message => message.field === "group-code",
          );

          return (
            <div className={css.initialGroupsStep_codeInput}>
              <GroupCodeInput
                label={t("package-wizard.groups.code")}
                value={row.choice.group.code}
                onChange={code =>
                  store.getState().setInitialGroupValues(row.subject.courseId, { code })
                }
                status={messages.length ? "error" : undefined}
                statusText={messages.map(message => message.message).join(", ")}
                onError={errors =>
                  setFormatErrors(current => ({
                    ...current,
                    [row.subject.courseId]: errors,
                  }))
                }
              />
            </div>
          );
        },
      },
      {
        key: "vacancies",
        header: t("package-wizard.groups.vacancies"),
        renderCell: data => {
          const row = data as SubjectConfiguration;
          if (row.choice.kind !== "create-new") return null;

          const messages = (messagesByCourseId[row.subject.courseId] ?? []).filter(
            message => message.field === "group-vacancies",
          );
          const hasInvalidSource = row.subject.vacancySource.kind === "invalid";
          const sourceMessage = hasInvalidSource
            ? t("package-wizard.validation.GROUP_VACANCIES_SOURCE_INVALID")
            : null;

          return (
            <div>
              <TextField
                className={css.initialGroupsStep_vacanciesField}
                label={t("package-wizard.groups.vacancies")}
                type="number"
                min={1}
                value={row.choice.group.vacancies}
                disabled={getLockedVacancies(row.subject.vacancySource) !== null}
                status={messages.length || sourceMessage ? "error" : undefined}
                statusText={sourceMessage ?? messages.map(message => message.message).join(", ")}
                onChange={event =>
                  store.getState().setInitialGroupValues(row.subject.courseId, {
                    vacancies: event.target.value,
                  })
                }
              />
            </div>
          );
        },
        desktopOptions: {
          headCellClassName: css.initialGroupsStep_vacanciesColumn,
        },
      },
    ],
    [messagesByCourseId, store, t],
  );

  const validateRows = () => {
    const errors = validateInitialGroupConfiguration(newLeagueRows, formatErrors, t);
    errors.forEach((messages, courseId) =>
      store.getState().setSubjectValidationMessages(courseId, messages),
    );
    return errors.size === 0;
  };

  const handleSubmit = async () => {
    if (!validateRows()) return;
    await onCreate();
  };

  const hasAnyNonSpecificColumnError = useMemo(() => {
    return Object.values(messagesByCourseId).some(messages =>
      messages.some(
        message =>
          message.field !== "group-code" &&
          message.field !== "group-vacancies" &&
          !message.discardable,
      ),
    );
  }, [messagesByCourseId]);

  return (
    <div className={css.initialGroupsStep}>
      <div className={css.initialGroupsStep_title}>
        <RoundButton size="sm" variant="ghost" icon="chevron-left" onClick={onBack} />
        <Heading type="h3">
          {t("package-wizard.groups.new-groups", { count: newLeagueRows.length })}
        </Heading>
      </div>

      <PackageConfigurationSummary packageDetails={packageDetails} />

      <CardNotification state="info" title={t("package-wizard.groups.info-title")}>
        {t("package-wizard.groups.info-description")}
      </CardNotification>

      <Table
        className={css.initialGroupsStep_table}
        columns={columns}
        data={rows}
        rowOptions={{
          renderCustomContent: hasAnyNonSpecificColumnError
            ? data => {
                const row = data as SubjectConfiguration;
                const messages = (messagesByCourseId[row.subject.courseId] ?? []).filter(
                  message =>
                    row.choice.kind !== "create-new" ||
                    (message.field !== "group-code" && message.field !== "group-vacancies"),
                );

                return <ValidationMessageList messages={messages} />;
              }
            : undefined,
        }}
        desktopVariant="regular"
      />

      {affectedRows > 0 && (
        <CardNotification
          state="warning"
          outlined
          title={t("package-wizard.groups.warning-title", {
            count: affectedRows,
            total: newLeagueRows.length,
          })}
        >
          {t("package-wizard.groups.warning-description", { count: affectedRows })}
        </CardNotification>
      )}

      <ValidationMessageList variant="notification" messages={globalMessages} />

      <div className={css.initialGroupsStep_actions}>
        <Button variant="ghost" disabled={isSubmitting} onClick={onBack}>
          {t("package-wizard.actions.back")}
        </Button>
        <Button disabled={isSubmitting} onClick={handleSubmit}>
          {t("package-wizard.actions.validate-create")}
        </Button>
      </div>
    </div>
  );
};

export default InitialGroupsStep;
